/**
 * Hook 内部异步请求共享工具
 *
 * 仅供 hooks 模块内部复用，不对外导出。
 */

import type { MutableRefObject } from 'react';

/** 将未知错误标准化为 Error 实例 */
export function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/** 是否为 AbortController 取消导致的错误 */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * 启动一次新的可取消请求：
 * - 先取消上一次请求
 * - 再创建并记录新的 controller
 */
export function startAbortableRequest(
  controllerRef: MutableRefObject<AbortController | null>,
): AbortController {
  controllerRef.current?.abort();
  const controller = new AbortController();
  controllerRef.current = controller;
  return controller;
}

/** 在请求未被取消时才更新 loading 状态 */
function setLoadingIfNotAborted(
  controller: AbortController,
  setLoading: (loading: boolean) => void,
): void {
  if (!controller.signal.aborted) {
    setLoading(false);
  }
}

/**
 * 统一处理可取消请求的异常：
 * - 忽略 abort 错误
 * - 在未取消时落库错误并回调
 */
export function handleAbortableRequestError(options: {
  error: unknown;
  controller: AbortController;
  setError: (error: Error) => void;
  onError?: (error: Error) => void;
}): void {
  const { error, controller, setError, onError } = options;
  if (controller.signal.aborted || isAbortError(error)) {
    return;
  }

  const normalized = normalizeError(error);
  setError(normalized);
  onError?.(normalized);
}

/** 统一处理业务操作失败（消息提示 + onError 回调） */
export function handleOperationError(options: {
  error: unknown;
  fallbackMessage: string;
  showError?: (message: string) => void;
  onError?: (error: Error) => void;
}): Error {
  const { error, fallbackMessage, showError, onError } = options;
  const normalized = normalizeError(error);
  showError?.(normalized.message || fallbackMessage);
  onError?.(normalized);
  return normalized;
}

/**
 * 执行可取消任务并统一处理异常与 loading 结束逻辑。
 *
 * 注意：任务内部应自行处理 `controller.signal.aborted` 的业务分支。
 */
export async function runAbortableTask<T>(options: {
  controller: AbortController;
  setLoading: (loading: boolean) => void;
  execute: () => Promise<T>;
  onError: (error: unknown) => void;
}): Promise<T | undefined> {
  const { controller, setLoading, execute, onError } = options;
  try {
    return await execute();
  } catch (error) {
    onError(error);
    return undefined;
  } finally {
    setLoadingIfNotAborted(controller, setLoading);
  }
}

/** 统一处理 API 业务失败（创建 Error + 设置错误 + 回调） */
export function handleApiFailure(options: {
  message?: string;
  fallbackMessage: string;
  setError: (error: Error) => void;
  onError?: (error: Error) => void;
}): Error {
  const { message, fallbackMessage, setError, onError } = options;
  const error = new Error(message || fallbackMessage);
  setError(error);
  onError?.(error);
  return error;
}
